/* eslint-disable react/prop-types */
import NextApp from 'next/app';
import getConfig from 'next/config';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import useSWR, { cache } from 'swr';
import { AnimateSharedLayout } from 'framer-motion';
import { ThemeProvider } from 'styled-components';
import { builder } from '@builder.io/react';

import { theme, GlobalStyle } from '@glowforge/gf-styled/theme';
import MetaTags from '@glowforge/ui-common/components/contentful-views/util/meta-tags';
import PageMetadata from '@glowforge/ui-common/components/contentful-views/page-metadata';
import { interpretMetadata } from '@glowforge/ui-common/models/page-metadata';
import { lazyLoadForceCheck } from '@glowforge/ui-common/components';
import Analytics from '../components/analytics';
import MessageBannerRebrand from '../components/message-banner/message-banner-rebrand';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import RouterProgressBar from '../components/router-progress-bar/router-progress-bar';
import { UserProvider } from '../context/UserContext';
import LocationProvider from '../context/LocationContext/LocationProvider';
import MessageCenterProvider from '../context/MessageCenterProvider';
import FeatureFlagsContext from '../context/FeatureFlagsContext';
import { ReferralInfoProvider } from '../context/ReferralInfoContext';
import ModalPriorityProvider from '../context/ModalPriorityContext/ModalPriorityProvider';
import { getDefaultMetadata, getGlobalFooterRebrand } from '../api/contentful';
import { getAllFlagsState, allFlagsStateVariable } from '../api/feature-flags';
import { REACT_ROOT } from '../constants';

import './app.less';
import './osano-rebrand.less';

/**
 * moving these external library less imports to js
 * allows them to be processed by @glowforge/postcss-gf-prefix-selector
 * postcss plugin. If imported via app.less, would not get
 * prefixed appropriately
 */
import '@glowforge/ui-common/stylesheets/base.less';

import '../polyfills';

/* Import Builder widget registers */

const {
  publicRuntimeConfig: { GF_DOTCOM_MADLIB_ENDPOINT },
} = getConfig();

const APP_CACHE_KEY = 'app';

const getMetadata = () => {
  const tags = new MetaTags();

  tags.push({ 'char-set': 'utf-8' });
  tags.push({ 'http-equiv': 'x-ua-compatible', content: 'ie=edge' });
  tags.push('viewport', 'width=device-width');
  tags.name('robots', 'noodp');
  tags.openGraph('locale', 'en_US');

  return tags.tags;
};

const getLinks = () => {
  const links = [
    {
      rel: 'alternate',
      type: 'application/rss+xml',
      title: 'Glowforge - the 3D Laser Printer » Home Comments Feed',
      href: 'https://blog.glowforge.com/feed/',
    },
    {
      rel: 'shortcut icon',
      href: `/dotcom/favicon.png`,
    },
  ];

  return links;
};

/**
 * Fetch data required by all pages
 */
const fetchData = async () => {
  const [defaultMetadata, allFlags, footerRebrandContent] = await Promise.all([
    getDefaultMetadata(),
    getAllFlagsState(),
    getGlobalFooterRebrand(),
  ]);

  return {
    defaultMetadata,
    allFlags,
    footerRebrandContent,
  };
};

const App = ({ initialData, countryCode, stateCode, pageProps, Component }) => {
  const appData = useSWR(APP_CACHE_KEY, () => fetchData(), {
    initialData,
    revalidateOnMount: true,
  });
  const { allFlags, defaultMetadata, footerRebrandContent } = appData.data;

  // `navbarProps` is a temporary workaround for STORE-4341. Eventually we
  // will need a way to compose layouts for different pages that doesn't rely
  // on this indirect approach
  const { navbarProps = {}, includeFooter = true, includeNav = true } = pageProps;
  const interpretedMetadata = interpretMetadata(defaultMetadata);
  const { pageTitle: defaultTitle, ...trimmedMetadata } = interpretedMetadata;

  useEffect(() => {
    // Avoid a bug caused by a race between preloaded CSS and JavaScript assets.
    // If JS executes before CSS is available lazy loaded image positions will
    // be different than their final position and we need to recheck.
    setTimeout(() => lazyLoadForceCheck());

    // If for some reason CSS was not applied on the next tick, check again when
    // window is loaded
    window.addEventListener('load', () => lazyLoadForceCheck());

    // Ensure that we're adding permissions for Osano in order to be GDPR compliant
    builder.canTrack = window.Osano
      && window.Osano.cm
      && window.Osano.cm.getConsent
      && window.Osano.cm.getConsent().ANALYTICS === 'ACCEPT';
  }, []);

  /* eslint-disable react/jsx-props-no-spreading, jsx-a11y/html-has-lang */
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <ModalPriorityProvider>
        <LocationProvider>
          <FeatureFlagsContext.Provider value={allFlags}>
            <UserProvider>
              <ReferralInfoProvider>
                <MessageCenterProvider>
                  <div className='app'>
                    <Analytics />
                    <Helmet
                      titleTemplate='%s | Glowforge'
                      meta={getMetadata()}
                      link={getLinks()}>
                      {/* Apply redesignClassName to body element */}
                      <html className='gf-styled gf-dotcom' />
                      <script type='text/javascript'>
                        {`
                            window._gf_react_root = '${REACT_ROOT}';
                            window.GF_MADLIB_ENDPOINT='${GF_DOTCOM_MADLIB_ENDPOINT}';
                            window.GF_COUNTRY_CODE='${countryCode}';
                            window.GF_STATE_CODE='${stateCode}';
                            window.${allFlagsStateVariable}=${JSON.stringify(
                          allFlags
                        )};
                          `}
                      </script>
                    </Helmet>
                    <PageMetadata
                      {...trimmedMetadata}
                      pageTitle={defaultTitle || 'Glowforge'}
                    />
                    <MessageBannerRebrand />
                    {includeNav ? <Navbar {...navbarProps} /> : null}
                    <RouterProgressBar />
                    <AnimateSharedLayout>
                      <Component {...pageProps} />
                    </AnimateSharedLayout>
                    <Footer
                      includeFooter={includeFooter}
                      footerRebrandContent={footerRebrandContent}
                    />
                  </div>
                </MessageCenterProvider>
              </ReferralInfoProvider>
            </UserProvider>
          </FeatureFlagsContext.Provider>
        </LocationProvider>
      </ModalPriorityProvider>
    </ThemeProvider>
  );
};

App.getInitialProps = async (appContext) => {
  const [initialData, appProps] = await Promise.all([
    cache.get(APP_CACHE_KEY) || fetchData(),
    NextApp.getInitialProps(appContext),
  ]);

  return {
    initialData,
    ...appProps,
  };
};

export default App;
